import { cueText, cueRecordingKey } from "./cueText";

const MAX_AGE_MS = 2000;
const PRIORITY = {
  SESSION_COMPLETE: 100,
  INTERVAL_START: 90, REST_START: 90, SET_REST_START: 90,
  SECTION_START: 80,
  SECTION_ANNOUNCE: 64,
  COUNTDOWN_1: 63, COUNTDOWN_2: 62, COUNTDOWN_3: 61,
};
const TONES = {
  prepare: { frequencies: [440], duration: 0.12 },
  countdown: { frequencies: [520], duration: 0.09 },
  rest: { frequencies: [660, 440], duration: 0.18 },
  setRest: { frequencies: [550, 440, 330], duration: 0.24 },
  newSet: { frequencies: [660, 880, 1100], duration: 0.22 },
  cooldown: { frequencies: [660, 550], duration: 0.2 },
  runout: { frequencies: [550, 660], duration: 0.2 },
  start: { frequencies: [660, 880], duration: 0.18 },
  end: { frequencies: [550, 330], duration: 0.16 },
  complete: { frequencies: [660, 880, 1100], duration: 0.3 },
};

function playbackOptions(options = {}) {
  return {
    language: options.language === "en" ? "en" : "de",
    mode: options.mode === undefined ? "tones" : options.mode,
    nowMs: options.nowMs,
  };
}

function validMode(mode) {
  return mode === "tones" || mode === "speech" || mode === "both";
}

// Latest instant wins. Historical end/count events are consumed but silent.
// Never replay a backlog; atMs is session time, emittedAtMs is monotonic time.
function createSelector() {
  const seen = new Set();
  const watermarks = new Map();
  return (cues, nowMs) => {
    let selected = null;
    for (const cue of Array.isArray(cues) ? cues : []) {
      if (!cue || typeof cue.id !== "string" || !cue.id || seen.has(cue.id)) continue;
      seen.add(cue.id);
      const priority = typeof cue.type === "string" && Object.prototype.hasOwnProperty.call(PRIORITY, cue.type) ? PRIORITY[cue.type] : 0;
      if (!priority || typeof cue.sessionId !== "string" || !cue.sessionId
        || !Number.isFinite(cue.atMs) || cue.atMs < 0 || !Number.isFinite(cue.emittedAtMs)) continue;
      if (nowMs !== undefined && (!Number.isFinite(nowMs)
        || nowMs < cue.emittedAtMs || nowMs - cue.emittedAtMs > MAX_AGE_MS)) continue;
      const watermark = watermarks.get(cue.sessionId);
      if (watermark && (cue.atMs < watermark.atMs
        || (cue.atMs === watermark.atMs && priority <= watermark.priority))) continue;
      if (!selected || cue.atMs > selected.cue.atMs
        || (cue.atMs === selected.cue.atMs && priority > selected.priority)) selected = { cue, priority };
    }
    if (!selected) return null;
    watermarks.set(selected.cue.sessionId, { atMs: selected.cue.atMs, priority: selected.priority });
    return { cue: selected.cue };
  };
}

function presentation(batch, language) {
  const { cue } = batch;
  const tone = cue.type === "SESSION_COMPLETE" ? "complete"
    : cue.type === "SECTION_ANNOUNCE" ? "prepare"
      : cue.type.startsWith("COUNTDOWN_") ? "countdown"
        : cue.type === "SET_REST_START" ? "setRest" : cue.type === "REST_START" ? "rest"
          : cue.params?.setNumber > 1 && cue.params?.intervalNumber === 1 ? "newSet"
            : cue.params?.kind === "cooldown" ? "cooldown" : cue.params?.kind === "runout" ? "runout" : "start";
  return { cue, tones: [tone], text: cueText(cue, language) };
}

/** Silent by default. playBatch returns zero or one selected IDs in both adapters.
 * stop() locks playback until the next explicit unlock; dispose() is terminal.
 * IDs remain consumed across stop/unlock. The returned ID is the primary cue;
 * diagnostics include its coalesced text and tones, never a playback queue.
 * Silent adapters are always idle and never emit interruption notifications.
 */
export function createSilentAudioAdapter() {
  const select = createSelector();
  const history = [];
  let unlocked = false;
  let disposed = false;
  return {
    async unlock({ userGesture } = {}) {
      if (disposed || userGesture !== true) return false;
      unlocked = true;
      return true;
    },
    playBatch(cues, options) {
      if (disposed) return [];
      const { language, mode, nowMs } = playbackOptions(options);
      const batch = select(cues, nowMs);
      if (!unlocked || !batch || !validMode(mode)) return [];
      const { cue, text, tones } = presentation(batch, language);
      history.push({ cue: { ...cue, params: { ...cue.params } }, language, mode, text, tones });
      return [cue.id];
    },
    stop() { unlocked = false; },
    dispose() { unlocked = false; disposed = true; },
    subscribeInterruption() { return () => {}; },
    whenIdle() { return Promise.resolve(); },
    get history() { return history.map((entry) => ({ ...entry, tones: [...entry.tones], cue: { ...entry.cue, params: { ...entry.cue.params } } })); },
    get playedIds() { return history.map(({ cue }) => cue.id); },
  };
}

function defaultContextFactory() {
  const Context = globalThis.AudioContext || globalThis.webkitAudioContext;
  return Context ? new Context() : null;
}

function foreground() {
  return typeof document === "undefined" || (!document.hidden && document.visibilityState !== "hidden");
}

function quietly(action) {
  try {
    const result = action();
    result?.catch?.(() => {});
  } catch (_error) { /* Audio teardown must also work after browser interruption. */ }
}

/** Foreground signal adapter. No speechSynthesis or TTS fallback.
 * Explicit optional same-origin recordings remain an extension point only;
 * active sessions use tones, with no recordings configured. No keepalive loops.
 * unlock must come from the user's gesture. stop/dispose cancel every channel.
 * whenIdle holds the session lease through the actual final signal.
 */
export function createBrowserAudioAdapter({
  contextFactory = defaultContextFactory,
  now = () => globalThis.performance.now(),
  onDiagnostic,
  recordings = {}, // Optional trusted, same-origin DE/EN clips. None shipped in this task.
  mediaFactory = src => new Audio(src),
} = {}) {
  const select = createSelector();
  let context = null;
  let activeTone = null;
  let unlocked = false;
  let disposed = false;
  let generation = 0;
  let unlocking = false;
  let updatingPlayback = false;
  let activeRecording = null;
  let recordingTimeout = null;
  let diagnostic = { code: "ready" };
  const report = (code, language, voice) => {
    diagnostic = { code, ...(language ? { language } : {}), ...(voice ? { voice: voice.name, voiceLanguage: voice.lang } : {}) };
    quietly(() => onDiagnostic?.({ ...diagnostic }));
  };
  const interruptionListeners = new Set();
  const idleWaiters = new Set();

  function resolveIdle() {
    if (updatingPlayback || activeTone || activeRecording) return;
    const waiting = [...idleWaiters];
    idleWaiters.clear();
    waiting.forEach(resolve => resolve());
  }

  function cancelTone() {
    const tone = activeTone;
    activeTone = null;
    if (!tone) return;
    if (tone.oscillator) tone.oscillator.onended = null;
    quietly(() => tone.oscillator?.stop());
    quietly(() => tone.oscillator?.disconnect());
    quietly(() => tone.gain?.disconnect());
  }

  function cancelRecording() {
    if (recordingTimeout !== null) clearTimeout(recordingTimeout);
    recordingTimeout = null;
    const media = activeRecording; activeRecording = null;
    if (!media) return;
    media.onended = media.onerror = null;
    quietly(() => media.pause());
    quietly(() => media.removeAttribute("src"));
  }

  function stop() {
    unlocked = false;
    unlocking = false;
    generation += 1;
    cancelTone();
    cancelRecording();
    if (context?.state === "running") quietly(() => context.suspend());
    resolveIdle();
  }

  function onStateChange() {
    const state = context?.state;
    if (!["suspended", "interrupted", "closed"].includes(state) || disposed) return;
    // Initial suspension while resume() is pending is normal. Once unlocked,
    // any non-running state is unexpected. stop() disarms this before suspend().
    if (!unlocked && !(unlocking && state !== "suspended")) return;
    stop();
    [...interruptionListeners].forEach(listener => {
      if (interruptionListeners.has(listener)) quietly(() => listener(state));
    });
  }

  function onVisibilityChange() {
    if (!foreground()) stop();
  }

  if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisibilityChange);
  if (typeof window !== "undefined") window.addEventListener("pagehide", stop);

  function playTones(names) {
    const specs = names.filter(name => typeof name === "string" && Object.prototype.hasOwnProperty.call(TONES, name)).map(name => TONES[name]);
    if (!specs.length) return false;
    const tone = { oscillator: null, gain: null };
    activeTone = tone;
    try {
      tone.oscillator = context.createOscillator();
      tone.gain = context.createGain();
      const start = context.currentTime;
      tone.oscillator.type = "sine";
      let end = start;
      specs.forEach((spec, toneIndex) => {
        const segmentStart = end + (toneIndex ? 0.02 : 0);
        end = segmentStart + spec.duration;
        spec.frequencies.forEach((frequency, index) => {
          tone.oscillator.frequency.setValueAtTime(frequency, segmentStart + index * spec.duration / spec.frequencies.length);
        });
        tone.gain.gain.setValueAtTime(0, segmentStart);
        tone.gain.gain.linearRampToValueAtTime(0.1, segmentStart + 0.01);
        tone.gain.gain.setValueAtTime(0.1, end - 0.02);
        tone.gain.gain.linearRampToValueAtTime(0, end);
      });
      tone.oscillator.connect(tone.gain);
      tone.gain.connect(context.destination);
      tone.oscillator.onended = () => {
        quietly(() => tone.oscillator.disconnect());
        quietly(() => tone.gain.disconnect());
        if (activeTone === tone) activeTone = null;
        resolveIdle();
      };
      tone.oscillator.start(start);
      tone.oscillator.stop(end);
      return true;
    } catch (_error) {
      cancelTone();
      return false;
    }
  }

  function recordingBudget(cue) {
    // Reserved recorded announcements must finish before T-2. Never alter the clock.
    const leadMs = cue.params?.leadMs;
    return leadMs === 7000 ? cue.emittedAtMs + leadMs - 2100 - now() : 0;
  }

  function playRecording(cue, language, fallback) {
    const clip = recordings?.[language]?.[cueRecordingKey(cue)];
    const budgetMs = recordingBudget(cue);
    // Only an explicit, trusted local asset mapping can opt into media playback.
    if (!clip || !/^\/[a-z0-9/_-]+\.(mp3|wav|m4a|ogg)$/i.test(clip.src || "")
      || !Number.isFinite(clip.durationMs) || clip.durationMs <= 0 || clip.durationMs > budgetMs - 100) return false;
    try {
      const media = mediaFactory(clip.src);
      activeRecording = media;
      const failed = () => {
        if (activeRecording !== media) return;
        cancelRecording(); report("recording_failed", language);
        if (unlocked && foreground()) fallback();
        resolveIdle();
      };
      media.onended = () => {
        if (activeRecording !== media) return;
        cancelRecording(); report("recording_finished", language); resolveIdle();
      };
      media.onerror = failed;
      recordingTimeout = setTimeout(() => {
        if (activeRecording !== media) return;
        cancelRecording(); report("recording_deadline_exceeded", language); resolveIdle();
      }, budgetMs);
      media.playbackRate = 1;
      media.play()?.catch(failed);
      report("recording_playing", language);
      return true;
    } catch { cancelRecording(); report("recording_failed", language); return false; }
  }

  return {
    async unlock({ userGesture } = {}) {
      if (disposed || userGesture !== true || !foreground()) return false;
      const attempt = generation;
      unlocking = true;
      try {
        if (!context || context.state === "closed") {
          context?.removeEventListener?.("statechange", onStateChange);
          context = contextFactory();
          context?.addEventListener?.("statechange", onStateChange);
        }
        if (!context) { report("web_audio_unavailable"); return false; }
        if (context.state !== "running") await context.resume();
        if (disposed || generation !== attempt || !foreground() || context.state !== "running") { report("audio_unlock_blocked"); return false; }
        unlocked = true;
        return true;
      } catch (_error) {
        if (generation === attempt) stop();
        report("audio_unlock_blocked");
        return false;
      } finally {
        if (generation === attempt) unlocking = false;
      }
    },
    playBatch(cues, options) {
      if (disposed) return [];
      const { language, mode, nowMs } = playbackOptions(options);
      const batch = select(cues, nowMs === undefined ? now() : nowMs);
      if (!foreground()) stop();
      else if (unlocked && context?.state !== "running") onStateChange();
      if (!unlocked || !validMode(mode)) return [];
      updatingPlayback = true;
      try {
        // Apply channel toggles even when there is no fresh cue to announce.
        if (mode === "tones") cancelRecording();
        if (mode === "speech") cancelTone();
        if (!batch) return [];
        const { cue, text, tones } = presentation(batch, language);
        // Replace both channels atomically; do not resolve idle between cues.
        cancelTone();
        cancelRecording();
        const fallback = () => {
          return mode !== "speech" && playTones(tones);
        };
        const played = mode !== "tones" && text && playRecording(cue, language, fallback);
        return played || fallback() ? [cue.id] : [];
      } finally {
        updatingPlayback = false;
        resolveIdle();
      }
    },
    stop,
    beginSession() { stop(); report("ready"); },
    getDiagnostics() { return { ...diagnostic }; },
    subscribeInterruption(listener) {
      if (disposed || typeof listener !== "function") return () => {};
      interruptionListeners.add(listener);
      return () => { interruptionListeners.delete(listener); };
    },
    whenIdle() {
      if (!activeTone && !activeRecording && !updatingPlayback) return Promise.resolve();
      return new Promise(resolve => idleWaiters.add(resolve));
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      stop();
      interruptionListeners.clear();
      context?.removeEventListener?.("statechange", onStateChange);
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisibilityChange);
      if (typeof window !== "undefined") window.removeEventListener("pagehide", stop);
      if (context && context.state !== "closed") quietly(() => context.close());
    },
  };
}
